import React, { useEffect, useState } from 'react';
import { Flame, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { fetchCommodities } from '../services/apiClient';
import TruthStateBadge from './TruthStateBadge';

function toneStyles(tone) {
  if (tone === 'warning') return { text: 'text-amber-200', bg: 'bg-amber-400/10', border: 'border-amber-400/30' };
  if (tone === 'positive') return { text: 'text-green-300', bg: 'bg-green-400/10', border: 'border-green-400/30' };
  return { text: 'text-blue-200', bg: 'bg-blue-400/10', border: 'border-blue-400/25' };
}

function Sparkline({ series = [], up }) {
  if (!series || series.length < 2) return null;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;
  const w = 64;
  const h = 20;
  const pts = series.map((v, i) => {
    const x = (i / (series.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={up ? '#34d399' : '#f87171'} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function ChangePill({ pct }) {
  if (pct == null) return <span className="text-mentor-muted text-xs">—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-green-300' : 'text-red-300'}`}>
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{up ? '+' : ''}{pct.toFixed(2)}%
    </span>
  );
}

export default function CommoditiesCard({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchCommodities()
      .then((res) => { if (active) setData(res); })
      .catch(() => { if (active) setData({ available: false }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return <p className="text-sm text-mentor-muted">Loading commodity prices…</p>;
  if (!data?.available || !data?.commodities?.length) {
    return <div className="rounded-xl border border-mentor-border/50 bg-white/[0.02] p-4 text-sm text-mentor-muted">Commodity prices are unavailable right now. This card fills in automatically once the feed responds.</div>;
  }

  const { commodities, reading, source } = data;
  const t = toneStyles(reading?.tone);
  const asOf = data.asOf ? new Date(data.asOf).toLocaleString() : undefined;

  return (
    <div className="space-y-4">
      <TruthStateBadge mode="LIVE" provider={source} asOf={asOf} note="Live commodity prices. Context only — does not change your Mission Control signal." />

      {/* Inflation read */}
      {reading && (
        <div className={`rounded-2xl border ${t.border} ${t.bg} p-4`}>
          <div className="flex items-center gap-2 mb-1">
            <Flame size={16} className={t.text} />
            <p className={`font-display font-bold text-sm ${t.text}`}>{reading.label}</p>
          </div>
          <p className="text-xs text-mentor-muted leading-relaxed">{reading.plainEnglish}</p>
          <p className="text-[11px] text-mentor-text/60 mt-2">Context only — does not change your Mission Control signal.</p>
        </div>
      )}

      {/* Commodities grid */}
      <div className={`grid gap-3 ${compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {commodities.map((c) => {
          const weekUp = (c.changeWeekPct ?? 0) >= 0;
          return (
            <div key={c.key} className="rounded-xl border border-mentor-border/50 bg-white/[0.02] p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-display font-bold text-sm text-mentor-text">{c.label}</span>
                <Sparkline series={c.series} up={weekUp} />
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-lg font-display font-black text-mentor-text">{c.price != null ? c.price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</span>
                <span className="text-[11px] text-mentor-muted">{c.unit}</span>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-[11px] text-mentor-muted">Day <ChangePill pct={c.changeDayPct} /></span>
                <span className="text-[11px] text-mentor-muted">Week <ChangePill pct={c.changeWeekPct} /></span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-mentor-muted/80 flex items-center gap-1"><Info size={11} /> Source: {source}. Rising energy &amp; metals together signals inflation pressure; broad declines ease it. Educational only, not financial advice.</p>
    </div>
  );
}
